import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { toast } from 'react-toastify';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

function getApiError(err) {
  const data = err.response?.data;
  if (!data) return err.message || 'Something went wrong';
  if (typeof data === 'string') return data;
  return (
    data.error ||
    data.message ||
    data.msg ||
    data.detail ||
    (Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : null) ||
    'Something went wrong'
  );
}

function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDoctor = location.pathname.includes('/doctor');

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);

  const loginPath = isDoctor ? '/doctor/login' : '/user/login';
  const accentClass = isDoctor ? 'btn-primary' : 'btn-primary';

  useEffect(() => {
    const initCsrf = async () => {
      try {
        await api.get(isDoctor ? 'doctor/login/' : 'user/login/');
      } catch {
        /* non-critical */
      }
    };
    initCsrf();
  }, [isDoctor]);

  const sendOtp = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      toast.warning('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      if (isDoctor) {
        await api.post('doctor/forgot-password/', { email: trimmed });
      } else {
        await api.post('user/password-reset-request/', { email: trimmed });
      }
      setOtpSent(true);
      setOtpVerified(false);
      toast.success('OTP sent to your email. Please check your inbox.');
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedOtp = otp.trim();

    if (!trimmedOtp) {
      toast.warning('Please enter the OTP from your email');
      return;
    }

    setLoading(true);
    try {
      if (isDoctor) {
        await api.post('doctor/forgot-password-verify-otp/', {
          email: trimmedEmail,
          otp: trimmedOtp,
        });
        setOtpVerified(true);
        toast.success('OTP verified. Set your new password below.');
      } else {
        setOtpVerified(true);
        toast.success('You can set your new password below.');
      }
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedOtp = otp.trim();

    if (!otpSent) {
      toast.warning('Please send OTP to your email first');
      return;
    }

    if (!trimmedOtp) {
      toast.warning('Please enter the OTP');
      return;
    }

    if (isDoctor && !otpVerified) {
      toast.warning('Please click "Verify OTP" before resetting password');
      return;
    }

    if (!PASSWORD_REGEX.test(newPassword)) {
      toast.warning(
        'Password must have 8+ characters, uppercase, lowercase, number and special character (@$!%*#?&)'
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }

    setLoading(true);
    try {
      if (isDoctor) {
        await api.post('doctor/reset-password/', {
          email: trimmedEmail,
          new_password: newPassword,
        });
      } else {
        await api.post('user/password-reset-confirm/', {
          email: trimmedEmail,
          otp: trimmedOtp,
          new_password: newPassword,
        });
      }
      toast.success('Password reset successful! Please login with your new password.');
      setTimeout(() => navigate(loginPath), 1500);
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100 d-flex align-items-center py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="text-center mb-4">
              <div className="bg-primary d-inline-flex p-3 rounded-circle shadow-sm mb-3">
                <i className="bi bi-key-fill fs-2 text-white" />
              </div>
              <h2 className="fw-bold text-primary">Reset Password</h2>
              <p className="text-muted mb-0">
                {isDoctor ? 'Doctor account' : 'Patient account'} — OTP will be sent to your email
              </p>
            </div>

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="card-body p-4 p-md-5">
                <form onSubmit={resetPassword}>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Email Address</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 text-muted">
                        <i className="bi bi-envelope" />
                      </span>
                      <input
                        type="email"
                        className="form-control border-start-0 bg-light"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className={`btn ${accentClass} w-100 mb-3`}
                    onClick={sendOtp}
                    disabled={loading}
                  >
                    {loading && !otpSent ? (
                      <span className="spinner-border spinner-border-sm me-2" />
                    ) : (
                      <i className="bi bi-send me-2" />
                    )}
                    Send OTP to Email
                  </button>

                  {otpSent && (
                    <>
                      <div className="alert alert-info py-2 small mb-3">
                        <i className="bi bi-info-circle me-1" />
                        OTP has been sent to <strong>{email.trim().toLowerCase()}</strong>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small fw-bold">Enter OTP</label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0 text-muted">
                            <i className="bi bi-shield-check" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-start-0 bg-light"
                            placeholder="6-digit OTP"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            maxLength={6}
                            inputMode="numeric"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-outline-primary w-100 mb-3"
                        onClick={verifyOtp}
                        disabled={loading || (isDoctor && otpVerified)}
                      >
                        {isDoctor && otpVerified ? (
                          <>
                            <i className="bi bi-check-circle me-2" />
                            OTP Verified
                          </>
                        ) : (
                          <>
                            <i className="bi bi-patch-check me-2" />
                            {isDoctor ? 'Verify OTP' : 'Verify OTP (optional)'}
                          </>
                        )}
                      </button>

                      {(!isDoctor || otpVerified) && (
                        <>
                          <div className="mb-3">
                            <label className="form-label small fw-bold">New Password</label>
                            <div className="input-group">
                              <span className="input-group-text bg-light border-end-0 text-muted">
                                <i className="bi bi-lock" />
                              </span>
                              <input
                                type="password"
                                className="form-control border-start-0 bg-light"
                                placeholder="New password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                disabled={loading || (isDoctor && !otpVerified)}
                              />
                            </div>
                          </div>

                          <div className="mb-4">
                            <label className="form-label small fw-bold">Confirm Password</label>
                            <div className="input-group">
                              <span className="input-group-text bg-light border-end-0 text-muted">
                                <i className="bi bi-lock-fill" />
                              </span>
                              <input
                                type="password"
                                className="form-control border-start-0 bg-light"
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={loading || (isDoctor && !otpVerified)}
                              />
                            </div>
                            <p className="text-muted small mt-2 mb-0">
                              Min 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special (@$!%*#?&)
                            </p>
                          </div>

                          <button
                            type="submit"
                            className={`btn ${accentClass} w-100 py-2 fw-bold`}
                            disabled={loading || (isDoctor && !otpVerified)}
                          >
                            {loading ? (
                              <span className="spinner-border spinner-border-sm me-2" />
                            ) : (
                              <i className="bi bi-arrow-repeat me-2" />
                            )}
                            Reset Password
                          </button>
                        </>
                      )}
                    </>
                  )}
                </form>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none"
                    onClick={() => navigate(loginPath)}
                  >
                    <i className="bi bi-arrow-left me-1" />
                    Back to Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
